import { useCallback, useEffect, useState } from 'react';

/**
 * Track the rendered width of an element (via ResizeObserver) so previews
 * can scale invitation text proportionally to their size.
 *
 * Returns `[ref, width]`; pass `ref` as the element's `ref` prop. A callback
 * ref is used so measuring starts even if the element mounts after a
 * loading state.
 */
export const useElementWidth = () => {
  const [node, setNode] = useState(null);
  const [width, setWidth] = useState(0);
  const ref = useCallback((el) => setNode(el), []);

  useEffect(() => {
    if (!node) return undefined;
    // offsetWidth is the layout width: a rotated or scaled ancestor (the home page's
    // fanned cards, a dialog's zoom-in) would inflate getBoundingClientRect's box.
    const update = () => setWidth(node.offsetWidth || node.getBoundingClientRect().width);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [ref, width];
};
